/* ═══════════════════════════════════════════════════════════════════
   Libellés lisibles des paramètres de conformité Intune.

   Deux familles de noms, relevées sur le tenant le 2026-09-25 (62 couples
   stratégie × paramètre) :

   1. Les paramètres NATIFS, préfixés par leur classe de stratégie :
      « AndroidDeviceOwnerCompliancePolicy.AdvancedThreatProtectionRequiredSecurityLevel »,
      80 caractères. Le préfixe ne dit rien qu'on ne sache déjà — le panneau
      nomme la stratégie —, et le nom court est partagé d'une plateforme à
      l'autre : « OsMinimumVersion » veut dire la même chose sous Android, iOS,
      macOS et Windows. D'où un dictionnaire indexé sur le nom court.

   2. Les scripts PERSONNALISÉS des stratégies « [CNS] Windows - Check … » :
      « BOOL », « Name », « Service ». Ces noms ne veulent rien dire seuls —
      cinq stratégies différentes évaluent un « BOOL ». En revanche,
      l'identifiant instancié porte la valeur attendue :
      « Name_IsEquals_Autopilot », « ServiceStatus_IsEquals_Running ». On
      l'extrait, et pour les noms génériques on reprend le sujet de la
      stratégie, qui est la seule chose qui les distingue.

   Le nom technique reste toujours rendu : c'est lui qu'on tape dans la
   recherche du centre d'administration Intune.
   ═══════════════════════════════════════════════════════════════════ */

const LIBELLES = {
    // Stratégie intégrée « Default Device Compliance Policy »
    RequireDeviceCompliancePolicyAssigned: 'Une stratégie de conformité est affectée',
    RequireRemainContact: 'Le poste remonte régulièrement',
    RequireUserExistence: "L'utilisateur inscrit existe toujours",

    // Intégrité de l'appareil
    SecurityBlockJailbrokenDevices: 'Appareil ni rooté ni jailbreaké',
    SecurityRequireIntuneAppIntegrity: "Intégrité de l'application Intune",
    SecurityRequireSafetyNetAttestationCertifiedDevice: 'Appareil certifié Play Integrity',
    SecurityRequiredAndroidSafetyNetEvaluationType: "Type d'évaluation Play Integrity",
    AdvancedThreatProtectionRequiredSecurityLevel: 'Niveau de menace Defender acceptable',
    SystemIntegrityProtectionEnabled: "Protection de l'intégrité du système (SIP)",
    SecureBootEnabled: 'Démarrage sécurisé activé',
    GatekeeperAllowedAppSource: "Gatekeeper : sources d'applications autorisées",
    FirewallEnabled: 'Pare-feu activé',

    // Chiffrement et système
    StorageRequireEncryption: 'Stockage chiffré',
    BitLockerEnabled: 'BitLocker activé',
    OsMinimumVersion: 'Version minimale du système',

    // Mots de passe
    PasswordRequired: 'Mot de passe exigé',
    PasscodeRequired: 'Code d\'accès exigé',
    PasswordRequiredType: 'Type de mot de passe',
    PasswordBlockSimple: 'Mots de passe simples interdits',
    PasscodeBlockSimple: 'Codes simples interdits',
    PasswordExpirationDays: 'Expiration du mot de passe',
    PasscodeExpirationDays: 'Expiration du code',
    PasswordMinimumLength: 'Longueur minimale du mot de passe',
    PasscodeMinimumLength: 'Longueur minimale du code',
    PasswordMinimumCharacterSetCount: 'Complexité du mot de passe',
    PasswordPreviousPasswordCountToBlock: 'Historique des mots de passe',
    PasscodePreviousPasscodeBlockCount: 'Historique des codes',
    NumberOfPasswordsToRemember: 'Historique des mots de passe',

    // Script « Check Security Apps : MDE »
    AMRunningMode: 'Antivirus Defender en mode actif',
    EDRBlockMode: 'EDR en mode blocage',
    EDRCloudBlock: 'Blocage cloud de l\'EDR',
    EnableNetworkProtection: 'Protection réseau activée',
    IsTamperProtected: 'Protection contre la falsification',
    RealTimeProtectionEnabled: 'Protection en temps réel',
    // « Service » apparaît dans MDE et dans SentinelOne, mais y dit la même
    // chose — le service de l'agent existe —, il n'est donc pas générique.
    Service: "Service de l'agent présent",

    // Script « Check Security App : SentinelOne »
    IsInstalled: 'Agent installé',
    SentinelLabsKey: 'Clé SentinelLabs présente',
    ServiceStatus: 'Service démarré',
    UninstallEntry: 'Entrée de désinstallation présente',

    // Scripts divers
    SID_Status: 'SID du compte local conforme',
};

// Noms qui ne distinguent rien : cinq stratégies évaluent un « BOOL », trois un
// « Name », chacune pour vérifier autre chose. Le libellé vient alors du sujet
// de la stratégie.
const GENERIQUES = new Set(['BOOL', 'Name']);

// « [CNS] Windows - Check Disable NetBios » → « Disable NetBios »
//
// Les préfixes de plateforme peuvent s'empiler — « [CNS] SRV - Windows - Check
// Security Apps : MDE » — d'où la boucle plutôt qu'un seul remplacement.
const PREFIXE_PLATEFORME = /^(Windows|MacOS|iOS|Android|SRV)(\s*\d+)?\s*-\s*/i;

export function sujetDeStrategie(nomStrategie) {
    let sujet = String(nomStrategie || '').replace(/^\[[^\]]*\]\s*/, '');
    while (PREFIXE_PLATEFORME.test(sujet)) sujet = sujet.replace(PREFIXE_PLATEFORME, '');
    return sujet.replace(/^Check\s+/i, '').trim();
}

// « AMRunningMode » → « AM Running Mode », pour ce que le dictionnaire ignore.
function deplierCamelCase(nom) {
    return String(nom || '')
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}

/**
 * @param {string} settingName  « Windows10CompliancePolicy.SecureBootEnabled », « BOOL »
 * @param {string} setting      Identifiant instancié : « BOOL_IsEquals_true »
 * @param {string} nomStrategie Nom de la stratégie qui porte le paramètre
 * @returns {{ libelle: string, attendu: string|null, technique: string }}
 */
export function libelleParametre(settingName, setting, nomStrategie) {
    const technique = settingName || setting || '';
    // Le nom court : ce qui suit le dernier point de la classe.
    const court = technique.includes('.') ? technique.slice(technique.lastIndexOf('.') + 1) : technique;

    // La valeur attendue, quand l'identifiant instancié la porte.
    const correspondance = String(setting || '').match(/_IsEquals_(.+)$/);
    const attendu = correspondance ? correspondance[1] : null;

    const libelle = GENERIQUES.has(court) && nomStrategie
        ? (sujetDeStrategie(nomStrategie) || court)
        : (LIBELLES[court] || deplierCamelCase(court));

    return { libelle, attendu, technique: court };
}
